import type { ClientComment } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextInput } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  KeyboardController,
  KeyboardStickyView
} from 'react-native-keyboard-controller';
import { IconButton } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useThrottledCallback } from 'use-debounce';

import { AddIcon, CheckIcon, CloseIcon, SendIcon } from '@/assets';
import { useCreateComment } from '@/features/api/hooks/grapevine-comment-events/useCreateComment';
import { useEditComment } from '@/features/api/hooks/grapevine-comment-events/useEditComment';
import { ChatMessageTextInput } from '@/features/chats/components/ChatMessageTextInput';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { AttachmentList } from '@/features/media/components/AttachmentList';
import { MediaItemPlaceholderList } from '@/features/media/components/MediaItemPlaceholderList';
import { MediaMenu } from '@/features/media/components/menu/MediaMenu';
import { useMediaPlaceholders } from '@/features/media/hooks/useMediaPlaceholder';
import { CommentEditorHeader } from '@/features/posts/components/comments/editor/CommentEditorHeader';
import { MAX_COMMENT_LENGTH } from '@/features/posts/constants/params';
import { SenderChipContainer } from '@/features/sender-selection/components/SenderChipContainer';
import { useValidatedSenderSelection } from '@/features/sender-selection/hooks/useValidatedSenderSelection';
import { SenderType } from '@/features/sender-selection/types/sender';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { hasItems } from '@/shared/utils/array';
import { sleep } from '@/shared/utils/async';
import { getPersonName } from '@/shared/utils/person';
import { isIOS } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      height: 'auto',
      // TODO theme color for boxShadow
      boxShadow: '0px -4px 4px -4px #2530591F',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      alignItems: 'stretch',
      backgroundColor: theme.colors.surfaceContainerLowest,
      padding: 12
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: theme.colors.primary
    },
    headerText: { flex: 1, color: theme.colors.onPrimary },
    images: { marginHorizontal: -16, paddingHorizontal: 16, paddingBottom: 16 },
    attachments: { paddingBottom: 16 },
    sender: {
      flexDirection: 'row',
      justifyContent: 'center',
      paddingBottom: 8
    },
    message: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'flex-end',
      gap: 8
    },
    text: { flex: 1 }
  });

type CommentEditorViewProps = {
  postId?: string;
  comment?: ClientComment;
  replyToComment?: ClientComment;
  onCancel: () => void;
  onCommentCreate: () => void;
};

export const CommentEditorView: FC<CommentEditorViewProps> = ({
  comment,
  replyToComment,
  postId,
  onCancel,
  onCommentCreate
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'components.commentEditor'
  });

  const { isFeatureEnabled } = useFeature();

  const createComment = useCreateComment();
  const changeComment = useEditComment();

  const textInputRef = useRef<TextInput>(null);
  const [text, setText] = useState('');

  const { sender } = useValidatedSenderSelection();

  // #region media
  const {
    onPickImage,
    onRemoveImage,
    onReorderImages,
    mediaItemPlaceholders,
    temporaryMediaItemIds,
    isImageUploadPending,
    onPickDocument,
    onRemoveDocument,
    documentItemPlaceholders,
    temporaryDocumentItemIds,
    isDocumentUploadPending,
    isUploadPending,
    attachmentFeatureValues
  } = useMediaPlaceholders({
    initialImages: comment?.images?.map((mediaItem) => ({
      mediaItemId: mediaItem.id,
      mediaItem
    })),
    initialDocuments: comment?.attachments?.map((documentItem) => ({
      documentItemId: documentItem.id,
      documentItem
    }))
  });

  // #endregion media

  const [showMenu, setShowMenu] = useState<boolean>(false);
  const canPickMoreImages = mediaItemPlaceholders.length < 10;
  const canPickMoreAttachments =
    documentItemPlaceholders.length <
    attachmentFeatureValues.maxNumberAttachmentsPerComment;

  const clearEditor = useCallback(async () => {
    // Note: we clear the editor after a short delay on iOS, as on iOS autocorrect may insert a corrected word into the input automatically
    if (isIOS) await sleep(100);
    // empty fields
    setText('');
    onCancel();
    KeyboardController.dismiss();
  }, [onCancel]);

  const submitComment = useCallback(() => {
    if (isEmptyOrNull(text)) return;
    if (isEmptyOrNull(postId)) return;

    if (comment) {
      changeComment.mutate({
        commentId: comment.id,
        useEmptyFields: true,
        text: text.trim(),
        documentItemPlaceHolders: documentItemPlaceholders,
        mediaItemPlaceHolders: mediaItemPlaceholders
      });
    } else {
      createComment.mutate(
        {
          postId: postId,
          replyToCommentId: replyToComment?.id,
          text: text.trim(),
          temporaryDocumentItemIds: temporaryDocumentItemIds,
          temporaryMediaItemIds: temporaryMediaItemIds,
          organizationId:
            sender.type === SenderType.ORGANIZATION
              ? sender.organizationId
              : undefined
        },
        { onSuccess: onCommentCreate }
      );
    }
    clearEditor();
  }, [
    changeComment,
    clearEditor,
    comment,
    createComment,
    documentItemPlaceholders,
    mediaItemPlaceholders,
    onCommentCreate,
    postId,
    replyToComment?.id,
    sender,
    temporaryDocumentItemIds,
    temporaryMediaItemIds,
    text
  ]);

  useEffect(() => {
    if (!comment) return;
    setText(comment.text ?? '');
  }, [comment]);

  const { bottom } = useSafeAreaInsets();
  const offset = { closed: -bottom, opened: 0 };

  useEffect(() => {
    if (comment || replyToComment) textInputRef.current?.focus();
  }, [comment, replyToComment]);

  const isMutationPending = useMemo(
    () => changeComment.isPending || createComment.isPending,
    [changeComment.isPending, createComment.isPending]
  );

  return (
    <KeyboardStickyView offset={offset}>
      <View>
        {comment && (
          <CommentEditorHeader label={t('editing')} onClose={clearEditor} />
        )}
        {replyToComment?.creator && (
          <CommentEditorHeader
            label={t('replyTo', {
              personName: getPersonName(replyToComment.creator)
            })}
            onClose={onCancel}
          />
        )}

        <View style={styles.container}>
          {(hasItems(mediaItemPlaceholders) || isImageUploadPending) && (
            <MediaItemPlaceholderList
              style={styles.images}
              items={mediaItemPlaceholders}
              onDelete={onRemoveImage}
              onReorder={onReorderImages}
              uploading={isImageUploadPending}
            />
          )}

          {(hasItems(documentItemPlaceholders) || isDocumentUploadPending) && (
            <AttachmentList
              style={styles.attachments}
              attachments={documentItemPlaceholders}
              onDelete={onRemoveDocument}
              actionIcon={CloseIcon}
              uploading={isDocumentUploadPending}
            />
          )}

          {!comment && (
            <View style={styles.sender}>
              <SenderChipContainer
                sender={sender}
                disabled={isMutationPending}
              />
            </View>
          )}

          <View style={styles.message}>
            <MediaMenu
              visible={showMenu}
              anchor={
                <IconButton
                  icon={({ color, size }) => (
                    <AddIcon fill={color} width={size} height={size} />
                  )}
                  size={24}
                  iconColor={theme.colors.primary}
                  style={{ margin: 0 }}
                  onPress={() => setShowMenu(true)}
                />
              }
              canPickImage
              disablePickImage={!canPickMoreImages}
              onPickImage={onPickImage}
              canPickDocument={isFeatureEnabled(FeaturesEnum.Attachments)}
              disablePickDocument={!canPickMoreAttachments}
              onPickDocument={onPickDocument}
              onDismiss={() => setShowMenu(false)}
            />

            <View style={styles.text}>
              <ChatMessageTextInput
                ref={textInputRef}
                value={text}
                placeholder={t('hint')}
                onValueChanged={setText}
                maxMessageLength={MAX_COMMENT_LENGTH}
              />
            </View>
            <IconButton
              mode="contained"
              icon={({ color, size }) =>
                comment ? (
                  <CheckIcon fill={color} width={size} height={size} />
                ) : (
                  <SendIcon fill={color} width={size} height={size} />
                )
              }
              theme={{
                colors: {
                  surfaceVariant: theme.colors.error,
                  primary: theme.colors.onPrimary
                }
              }}
              size={24}
              style={{ margin: 0 }}
              disabled={
                isEmptyOrNull(text) || isMutationPending || isUploadPending
              }
              onPress={useThrottledCallback(submitComment, 1000)}
            />
          </View>
        </View>
      </View>
    </KeyboardStickyView>
  );
};
